"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _utils = require("../utils");
var _InputSupports = _interopRequireDefault(require("../InputSupports"));
var _TextInputBase = _interopRequireDefault(require("./TextInputBase"));
var _ThemeProvider = require("../ThemeProvider");
var _propTypes = _interopRequireDefault(require("./propTypes"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.focusHandlerProps, _utils.inputSupportsProps, _utils.textInputHandlerProps, _utils.textInputProps, _utils.viewProps]);

/**
 * Use to collect long-form information such as product feedback or support queries.
 * Due to React Native's implementation of `TextInput` it's not possible to access the current value by passing a ref.
 * The `TextArea` height will automatically increase when new lines are added - this behavior can be limited within the theme.
 *
 * ## Controlled component
 * If it is required that the state of the `TextArea` be controlled by the application or other external methods,
 * `value` and `onChange` props must be passed to the `TextArea`.
 * If the `TextArea` value should not be changed by user input, a `readOnly` prop must be provided.
 *
 * ## Uncontrolled component
 * If it is not necessary to control the `TextArea` state, you can create one without a value prop.
 * In this case its value can be accessed within the `onChange` callback.
 *
 * ## Getting feedback for entered values
 * Use the `feedback` prop to give the user feedback regarding their input.
 * You can affirm that the user's input was correct, or highlight errors that must be corrected, using the `validation` prop.
 * Keep feedback text as brief as possible, should be limited to text and links.
 *
 * ## Supplying a hint
 * Use the `hint` prop to succinctly clarify attributes of the expected input data, such as the expected format,
 * or an indicator that the field is optional.
 * It is a more usable and accessible option than the HTML `placeholder` attribute.
 */
const TextArea = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    tokens,
    variant = {},
    ...rest
  } = _ref;
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('TextArea', tokens, variant);
  const [inputHeight, setInputHeight] = _react.default.useState();
  const isUpdatingHeight = _react.default.useRef(false);

  // adjust the text area's height as new lines are added to the content
  const onHeightChange = _ref2 => {
    let {
      nativeEvent: {
        contentSize: {
          height
        }
      }
    } = _ref2;
    // on native this is happens out of the box
    if (_Platform.default.OS === 'web' && !isUpdatingHeight.current) {
      isUpdatingHeight.current = true;
      setInputHeight(height);
      // setTimeout is used to “wait” for the next update cycle before allowing a new height adjustment,
      // avoiding multiple updates in the same render and preventing a possible infinite loop or constant re-renders.
      setTimeout(() => {
        isUpdatingHeight.current = false;
      }, 0);
    } else {
      setInputHeight(null);
    }
  };
  const {
    supportsProps,
    ...selectedProps
  } = selectProps(rest);
  const inputProps = {
    ...selectedProps,
    multiline: true,
    onContentSizeChange: onHeightChange,
    height: inputHeight,
    tokens: themeTokens
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_InputSupports.default, {
    ...supportsProps,
    inputValue: selectedProps?.value,
    children: _ref3 => {
      let {
        inputId,
        ...props
      } = _ref3;
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_TextInputBase.default, {
        ref: ref,
        nativeID: inputId,
        ...props,
        ...inputProps,
        variant: {
          ...variant,
          validation: props.validation
        }
      });
    }
  });
});
TextArea.displayName = 'TextArea';
TextArea.propTypes = {
  ...selectedSystemPropTypes,
  ..._propTypes.default,
  tokens: (0, _utils.getTokensPropType)('TextArea', 'TextInput'),
  // TODO: support both TextArea and TextInput tokens
  variant: _utils.variantProp.propType
};
var _default = exports.default = TextArea;